import { useUsageLimits } from "@/hooks/useUsageLimits";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UsageLimitBannerProps {
  type: "documents" | "ai_searches" | "pdf_actions";
  showUpgrade?: boolean;
}

const TYPE_LABELS = {
  documents: "Documents",
  ai_searches: "Recherches IA",
  pdf_actions: "Actions PDF",
};

const TYPE_PERIODS = {
  documents: "",
  ai_searches: "aujourd'hui",
  pdf_actions: "ce mois",
};

export function UsageLimitBanner({ type, showUpgrade = true }: UsageLimitBannerProps) {
  const { usage, limits, getUsagePercentage, getRemainingUsage, isLoading } = useUsageLimits();
  const navigate = useNavigate();

  if (isLoading) return null;

  const percentage = getUsagePercentage(type);
  const remaining = getRemainingUsage(type);
  const isUnlimited = limits[type] === -1;
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 100;

  if (isUnlimited) return null;

  return (
    <div
      className={`rounded-lg border p-4 ${
        isAtLimit
          ? "border-destructive bg-destructive/10"
          : isNearLimit
          ? "border-warning bg-warning/10"
          : "border-border bg-muted/50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isNearLimit && <AlertTriangle className="w-4 h-4 text-warning" />}
          <span className="text-sm font-medium">
            {TYPE_LABELS[type]} {TYPE_PERIODS[type] && `(${TYPE_PERIODS[type]})`}
          </span>
        </div>
        <span className="text-sm text-muted-foreground">
          {usage[type]} / {limits[type]}
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
      {isAtLimit && showUpgrade && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-destructive">
            Limite atteinte
          </span>
          <Button size="sm" onClick={() => navigate("/pricing")}>
            <Zap className="w-4 h-4 mr-1" />
            Passer au niveau supérieur
          </Button>
        </div>
      )}
      {isNearLimit && !isAtLimit && (
        <p className="mt-2 text-sm text-muted-foreground">
          Plus que {remaining} {TYPE_LABELS[type].toLowerCase()} disponibles
        </p>
      )}
    </div>
  );
}
